import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { useFlights, useRuns } from "../../api/hooks";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText, fmtDate, fmtTick } from "../../components/ui";
import { CodeEditor } from "../../editor/CodeEditor";
import {
  goldenFileUrl,
  suggestScenario,
  useDeleteGolden,
  useDeleteScenarioFile,
  useExportPytest,
  useGoldenDetail,
  useRunTests,
  useSaveScenarioFile,
  useTestsOverview,
  validateScenario,
  type CheckResult,
  type ScenarioResult,
  type VerifyReport,
} from "./verifyApi";
import "./verify.css";

const NEW_SCENARIO = `# One scenario per file, or a "scenarios:" list. Flight is a workspace flight id,
# a path (\${ENV} expands) or "synthetic" for the built-in demo profile.
name: my scenario
flight: synthetic
# inputs: {limits.alt_tol: 10}      # dotted overrides of inputs/*.yaml
# golden: my_scenario              # compare every rendered file against tests/golden/<name>/
expect:
  - {kind: no_errors}
  - {kind: fires, trigger: my_trigger, count: 1}
  # - {kind: fire_at, trigger: my_trigger, label: LABEL, t: 120, tol: 1}
  # - {kind: fire_window, trigger: my_trigger, start: 120, end: 129}
  # - {kind: value, trigger: my_trigger, key: n1, min: 85}
  # - {kind: param_at, mnemonic: ALT, t: 120, max: 1000}
  # - {kind: report_contains, report: my_report, text: "XA-HPY"}
  # - {kind: report_count, count: 2}
`;

type Tab = "results" | "editor" | "baselines";

export function TestsPage() {
  const session = useSession();
  const { projectId } = session;
  const overview = useTestsOverview(projectId);
  const run = useRunTests(projectId);
  const save = useSaveScenarioFile(projectId);
  const del = useDeleteScenarioFile(projectId);
  const exportPytest = useExportPytest(projectId);
  const [tab, setTab] = useState<Tab>("results");
  const [report, setReport] = useState<VerifyReport | null>(null);
  const [selected, setSelected] = useState<string | null>(null); // scenario name
  const [editing, setEditing] = useState<{ filename: string; content: string; dirty: boolean } | null>(null);
  const [problems, setProblems] = useState<{ line: number; col: number; message: string; severity: "error" | "warning" | "info" }[]>([]);
  const [goldenName, setGoldenName] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    if (!report && overview.data?.last) setReport(overview.data.last);
  }, [overview.data, report]);

  const results = report?.results ?? [];
  const current = results.find((r) => r.name === selected) ?? null;
  const checkNames = useMemo(() => {
    const names: string[] = [];
    for (const r of results) for (const c of r.checks) if (!names.includes(c.name)) names.push(c.name);
    return names;
  }, [results]);

  const doRun = async (update_golden: boolean, file?: string) => {
    if (!projectId) return;
    if (update_golden && !confirm("Re-capture golden baselines from this run? Existing baselines for the selected scenarios are overwritten.")) return;
    try {
      const r = await run.mutateAsync({ update_golden, file });
      setReport(r);
      setTab("results");
      if (!selected || !r.results.some((x) => x.name === selected)) setSelected(r.results[0]?.name ?? null);
      session.toast(r.passed ? `Tests passed (${r.summary.checks} checks)` : `${r.summary.failed + r.summary.error} scenario(s) failed`, r.passed ? "info" : "error");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  const openFile = async (path: string) => {
    if (!projectId) return;
    const filename = path.split("/").pop()!;
    try {
      const body = await api.get<{ content: string }>(`/projects/${projectId}/files/${path}`);
      setEditing({ filename, content: body.content, dirty: false });
      setProblems([]);
      setTab("editor");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  const newFile = () => {
    const n = (overview.data?.files.length ?? 0) + 1;
    setEditing({ filename: `scenario_${n}.yaml`, content: NEW_SCENARIO, dirty: true });
    setProblems([]);
    setTab("editor");
  };

  const validate = useCallback(async () => {
    if (!projectId || !editing) return true;
    const res = await validateScenario(projectId, editing.content);
    setProblems(res.problems.map((p) => ({ line: p.line, col: p.col, message: p.message, severity: "error" as const })));
    return res.problems.length === 0;
  }, [projectId, editing]);

  const doSave = async () => {
    if (!editing) return;
    try {
      const r = await save.mutateAsync({ filename: editing.filename, content: editing.content });
      setEditing({ ...editing, dirty: false });
      setProblems([]);
      session.toast(`Saved ${r.path} (${r.expanded} scenario${r.expanded === 1 ? "" : "s"})`, "info");
    } catch (e) {
      setProblems([{ line: 1, col: 0, message: errText(e), severity: "error" }]);
    }
  };

  if (!projectId) return <Empty title="Select a project" />;
  const files = overview.data?.files ?? [];
  const baselines = overview.data?.baselines ?? [];

  return (
    <>
    <Split id="tests" className="tests-page" sizes={[320, "1fr"]} minSizes={[200, 400]}>
      <div className="pane">
        <div className="pane-head">
          Tests
          <span className="spacer" />
          <button className="btn sm" onClick={newFile} title="New scenario file">
            + Scenario
          </button>
          <button className="btn sm" onClick={() => setSuggesting(true)} title="Build a scenario from a finished run">
            From run…
          </button>
        </div>
        <div className="col" style={{ padding: 8, gap: 6, borderBottom: "1px solid var(--rs-border)" }}>
          <div className="row">
            <button className="btn sm primary" onClick={() => doRun(false)} disabled={run.isPending || !files.length}>
              {run.isPending ? <Spinner /> : "▶ Run all"}
            </button>
            <button className="btn sm" onClick={() => doRun(true)} disabled={run.isPending || !files.length} title="Run and (re)capture golden baselines">
              ⟳ Update golden
            </button>
            <span className="spacer" />
            {report ? <LastBadge report={report} /> : null}
          </div>
          {report?.ran_at ? (
            <div className="small dim">
              last run {fmtDate(report.ran_at)} · {report.summary.checks - report.summary.checks_failed}/{report.summary.checks} checks in {report.elapsed_s.toFixed(1)}s
            </div>
          ) : null}
        </div>
        <div className="list">
          <div className="rail-group">Scenario files</div>
          {files.map((f) => (
            <div key={f.path} className={`list-item${editing?.filename === f.name && tab === "editor" ? " sel" : ""}`} onClick={() => openFile(f.path)}>
              <div className="name">
                <div className="row">
                  <span className="mono">{f.name}</span>
                  {f.problems.length ? <span className="badge err">invalid</span> : <span className="badge">{f.scenarios.reduce((n, s) => n + (s.expanded?.length ?? 1), 0)}</span>}
                </div>
                <div className="small dim">{f.problems.length ? f.problems[0].message : f.scenarios.map((s) => s.name).join(", ")}</div>
              </div>
              <button
                className="btn ghost sm"
                title="Run this file"
                onClick={(e) => {
                  e.stopPropagation();
                  doRun(false, f.path);
                }}
              >
                ▶
              </button>
              <button
                className="btn ghost sm"
                title="Delete file"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!confirm(`Delete ${f.name}?`)) return;
                  await del.mutateAsync(f.name);
                  if (editing?.filename === f.name) setEditing(null);
                }}
              >
                ✕
              </button>
            </div>
          ))}
          {!files.length && !overview.isLoading ? (
            <div className="dim small" style={{ padding: 10 }}>
              No scenarios yet. Add one, or build it from a run.
            </div>
          ) : null}
          <div className="rail-group">Golden baselines</div>
          {baselines.map((b) => (
            <div
              key={b.name}
              className={`list-item${goldenName === b.name && tab === "baselines" ? " sel" : ""}`}
              onClick={() => {
                setGoldenName(b.name);
                setTab("baselines");
              }}
            >
              <div className="name">
                <div className="row">
                  <span className="mono">{b.name}</span>
                  <span className="badge">{b.outputs.length} files</span>
                </div>
                <div className="small dim">
                  {b.fires} fire(s) · {b.flight_id ?? ""} · {fmtDate(new Date(b.captured * 1000).toISOString())}
                </div>
              </div>
            </div>
          ))}
          {!baselines.length ? (
            <div className="dim small" style={{ padding: 10 }}>
              No baselines. Give a scenario a <code>golden:</code> name and run “Update golden”.
            </div>
          ) : null}
          <div className="rail-group">pytest</div>
          <div className="col" style={{ padding: "4px 10px", gap: 4 }}>
            <div className="small dim">
              {overview.data?.pytest.exists ? (
                <>
                  <code>{overview.data.pytest.path}</code> {overview.data.pytest.generated ? "(generated)" : "(hand-edited)"}
                </>
              ) : (
                "Not exported. Generates tests/test_project.py so the project runs under plain pytest."
              )}
            </div>
            <div className="row">
              <button
                className="btn sm"
                disabled={exportPytest.isPending}
                onClick={async () => {
                  try {
                    const r = await exportPytest.mutateAsync();
                    session.toast(`Wrote ${r.written.join(", ")} — run: ${r.command}`, "info");
                  } catch (e) {
                    session.toast(errText(e), "error");
                  }
                }}
              >
                {overview.data?.pytest.exists ? "Regenerate pytest files" : "Export to pytest"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pane">
        <div className="tabs">
          <button className={tab === "results" ? "on" : ""} onClick={() => setTab("results")}>
            Results {report ? <span className={`badge ${report.passed ? "ok" : "err"}`}>{report.summary.passed}/{report.summary.scenarios}</span> : null}
          </button>
          <button className={tab === "editor" ? "on" : ""} onClick={() => setTab("editor")}>
            Editor {editing ? <span className="badge">{editing.filename}{editing.dirty ? " •" : ""}</span> : null}
          </button>
          <button className={tab === "baselines" ? "on" : ""} onClick={() => setTab("baselines")}>
            Baseline {goldenName ? <span className="badge">{goldenName}</span> : null}
          </button>
        </div>
        <div className="pane-body" style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
          {tab === "results" ? (
            results.length ? (
              <Split id="tests-results" className="fill" sizes={["45fr", "55fr"]} minSizes={[280, 280]} collapsible={[false, true]}>
                <div className="col" style={{ overflow: "auto", padding: 8 }}>
                  <ScenarioMatrix results={results} checkNames={checkNames} selected={selected} onSelect={setSelected} />
                </div>
                <div className="col" style={{ overflow: "auto", padding: 10 }}>
                  {current ? <ScenarioDetail res={current} projectId={projectId} /> : <Empty title="Pick a scenario" />}
                </div>
              </Split>
            ) : (
              <Empty title="No results yet">
                <div className="dim small">Run the scenarios to see the matrix. Cells are one assertion each; click a row for details and golden drift.</div>
              </Empty>
            )
          ) : null}
          {tab === "editor" ? (
            editing ? (
              <>
                <div className="row" style={{ padding: "6px 10px", borderBottom: "1px solid var(--rs-border)" }}>
                  <span className="dim small">tests/scenarios/</span>
                  <input
                    className="inp sm mono"
                    value={editing.filename}
                    onChange={(e) => setEditing({ ...editing, filename: e.target.value, dirty: true })}
                    style={{ width: 220 }}
                  />
                  <span className="spacer" />
                  <button className="btn sm" onClick={validate}>
                    Validate
                  </button>
                  <button className="btn sm primary" onClick={doSave} disabled={save.isPending || !editing.dirty}>
                    Save
                  </button>
                  <button
                    className="btn sm"
                    onClick={async () => {
                      if (editing.dirty) await doSave();
                      doRun(false, `tests/scenarios/${editing.filename}`);
                    }}
                    disabled={run.isPending}
                  >
                    ▶ Run file
                  </button>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <CodeEditor
                    path={`tests/scenarios/${editing.filename}`}
                    value={editing.content}
                    language="yaml"
                    onChange={(v) => setEditing({ ...editing, content: v, dirty: true })}
                    onSave={doSave}
                    markers={problems}
                  />
                </div>
                {problems.length ? (
                  <div className="problem" style={{ margin: 6 }}>
                    <span className="sev-error">error</span> <span>{problems[0].message}</span>
                  </div>
                ) : null}
              </>
            ) : (
              <Empty title="No file open">
                <div className="dim small">Pick a scenario file on the left, create a new one, or build one from a run.</div>
              </Empty>
            )
          ) : null}
          {tab === "baselines" ? (
            goldenName ? (
              <BaselineView projectId={projectId} name={goldenName} onDeleted={() => setGoldenName(null)} />
            ) : (
              <Empty title="Pick a baseline" />
            )
          ) : null}
        </div>
      </div>
    </Split>
      {suggesting ? (
        <SuggestDialog
          projectId={projectId}
          onClose={() => setSuggesting(false)}
          onSuggested={(filename, content) => {
            setEditing({ filename, content, dirty: true });
            setProblems([]);
            setTab("editor");
            setSuggesting(false);
          }}
        />
      ) : null}
    </>
  );
}

function LastBadge({ report }: { report: VerifyReport }) {
  const s = report.summary;
  return (
    <span className="row small" style={{ gap: 4 }}>
      {s.passed ? <span className="badge ok">{s.passed} pass</span> : null}
      {s.failed ? <span className="badge err">{s.failed} fail</span> : null}
      {s.error ? <span className="badge err">{s.error} error</span> : null}
      {s.skipped ? <span className="badge">{s.skipped} skip</span> : null}
    </span>
  );
}

function ScenarioMatrix({
  results,
  checkNames,
  selected,
  onSelect,
}: {
  results: ScenarioResult[];
  checkNames: string[];
  selected: string | null;
  onSelect: (name: string) => void;
}) {
  return (
    <table className="tbl matrix">
      <thead>
        <tr>
          <th>Scenario</th>
          <th>Status</th>
          {checkNames.map((n) => (
            <th key={n} className="rot" title={n}>
              <span>{n}</span>
            </th>
          ))}
          <th>Golden</th>
        </tr>
      </thead>
      <tbody>
        {results.map((r) => {
          const byName = new Map(r.checks.map((c) => [c.name, c]));
          return (
            <tr key={r.name} className={`clickable${r.name === selected ? " sel" : ""}`} onClick={() => onSelect(r.name)}>
              <td>
                <div className="mono">{r.name}</div>
                <div className="small dim">{r.file}</div>
              </td>
              <td>
                <span className={`badge ${r.status === "passed" ? "ok" : r.status === "skipped" ? "" : "err"}`}>{r.status}</span>
              </td>
              {checkNames.map((n) => {
                const c = byName.get(n);
                return (
                  <td key={n} className="cell" title={c ? `${n}: ${c.message}` : ""}>
                    {c ? <span className={`dot ${c.passed ? "ok" : "bad"}`} /> : <span className="faint">·</span>}
                  </td>
                );
              })}
              <td>
                {r.golden ? (
                  <span className={`badge ${r.golden.passed ? "ok" : r.golden.exists ? "err" : "warn"}`}>
                    {r.golden.passed ? "match" : r.golden.exists ? `${r.golden.drift.length} drift` : "none"}
                  </span>
                ) : (
                  <span className="faint">—</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ScenarioDetail({ res, projectId }: { res: ScenarioResult; projectId: string }) {
  const navigate = useNavigate();
  const session = useSession();
  return (
    <div className="col" style={{ gap: 12 }}>
      <div className="row wrap">
        <span className={`badge ${res.status === "passed" ? "ok" : res.status === "skipped" ? "" : "err"}`}>{res.status}</span>
        <span className="mono">{res.name}</span>
        <span className="dim small">
          {res.file} · flight {res.flight_id ?? res.flight} · {res.elapsed_s.toFixed(2)}s
        </span>
        <span className="spacer" />
        {res.flight_id ? (
          <button
            className="btn ghost sm"
            title="Open this flight in Analysis"
            onClick={() => {
              session.setActiveFlight(res.flight_id!);
              navigate("/analysis");
            }}
          >
            📈
          </button>
        ) : null}
      </div>
      <div className="small">{res.message}</div>
      {Object.keys(res.inputs ?? {}).length ? (
        <div className="small dim">
          inputs: <code>{JSON.stringify(res.inputs)}</code>
        </div>
      ) : null}
      {res.checks.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th />
              <th>Check</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {res.checks.map((c, i) => (
              <CheckRow key={i} c={c} />
            ))}
          </tbody>
        </table>
      ) : null}
      {res.golden && !res.golden.passed && res.golden.exists ? (
        <div>
          <div className="section-title">Golden drift ({res.golden.drift.length})</div>
          {res.golden.drift.map((d, i) => (
            <details key={i} className="problem" open={i === 0}>
              <summary>
                <span className={`badge ${d.status === "changed" ? "warn" : "err"}`}>{d.kind} {d.status}</span> <span className="mono">{d.name}</span>{" "}
                <span className="dim small">{d.detail}</span>
              </summary>
              {d.diff ? <pre className="diff">{colourDiff(d.diff)}</pre> : null}
            </details>
          ))}
        </div>
      ) : null}
      {res.golden && !res.golden.exists ? (
        <div className="problem">
          <span className="sev-warning">warning</span> <span>{res.golden.message}</span>
        </div>
      ) : null}
      {res.fires.length ? (
        <div>
          <div className="section-title">Fires ({res.fires.length})</div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Trigger</th>
                <th>Label</th>
                <th>t</th>
                <th>Window</th>
              </tr>
            </thead>
            <tbody>
              {res.fires.map((f, i) => (
                <tr key={i}>
                  <td className="mono">{f.trigger}</td>
                  <td>{f.label}</td>
                  <td className="num">{fmtTick(f.t)}</td>
                  <td className="num">
                    {fmtTick(f.window[0])}–{fmtTick(f.window[1])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {res.outputs.length ? (
        <div>
          <div className="section-title">Rendered files ({res.outputs.length})</div>
          {res.outputs.map((o) => (
            <div key={o} className="small mono">
              {res.golden_name && res.golden?.exists ? (
                <a href={goldenFileUrl(projectId, res.golden_name, o)} target="_blank" rel="noreferrer">
                  {o}
                </a>
              ) : (
                o
              )}
            </div>
          ))}
        </div>
      ) : null}
      {res.errors.length ? (
        <div>
          <div className="section-title">Errors</div>
          {res.errors.map((e, i) => (
            <pre key={i} className="problem err mono small" style={{ whiteSpace: "pre-wrap" }}>
              {e}
            </pre>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CheckRow({ c }: { c: CheckResult }) {
  const [open, setOpen] = useState(false);
  const hasDetail = c.expected !== null || (c.actual !== null && c.kind !== "golden");
  return (
    <>
      <tr className={hasDetail ? "clickable" : ""} onClick={() => hasDetail && setOpen(!open)}>
        <td>
          <span className={`dot ${c.passed ? "ok" : "bad"}`} />
        </td>
        <td>
          <span className="mono">{c.name}</span> <span className="faint small">{c.kind}</span>
        </td>
        <td className={c.passed ? "" : "sev-error"}>{c.message}</td>
      </tr>
      {open ? (
        <tr>
          <td />
          <td colSpan={2} className="small dim">
            expected <code>{JSON.stringify(c.expected)}</code> · actual <code>{JSON.stringify(c.actual)}</code>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function colourDiff(diff: string) {
  return diff.split("\n").map((line, i) => (
    <div key={i} className={line.startsWith("+") && !line.startsWith("+++") ? "add" : line.startsWith("-") && !line.startsWith("---") ? "del" : line.startsWith("@@") ? "hunk" : ""}>
      {line}
    </div>
  ));
}

function BaselineView({ projectId, name, onDeleted }: { projectId: string; name: string; onDeleted: () => void }) {
  const detail = useGoldenDetail(projectId, name);
  const del = useDeleteGolden(projectId);
  const [file, setFile] = useState<string | null>(null);
  const [text, setText] = useState("");
  useEffect(() => {
    setFile(null);
    setText("");
  }, [name]);
  const open = async (f: string) => {
    setFile(f);
    const res = await fetch(goldenFileUrl(projectId, name, f), { credentials: "include" });
    setText(await res.text());
  };
  if (detail.isLoading) return <Spinner label="loading baseline" />;
  if (!detail.data) return <Empty title="Baseline not found" />;
  const summary = detail.data.summary as { fires?: { trigger: string; label: string; t: number; window: [number, number] }[]; flight_id?: string; tail?: string };
  return (
    <Split id="tests-baseline" className="fill" sizes={["38fr", "62fr"]} minSizes={[240, 280]} collapsible={[true, false]}>
      <div className="col" style={{ padding: 10, gap: 10, overflow: "auto" }}>
        <div className="row">
          <span className="mono">{name}</span>
          <span className="dim small">
            {summary.tail ?? ""} {summary.flight_id ?? ""}
          </span>
          <span className="spacer" />
          <button
            className="btn sm danger"
            onClick={async () => {
              if (!confirm(`Delete baseline ${name}? The next run with "Update golden" recaptures it.`)) return;
              await del.mutateAsync(name);
              onDeleted();
            }}
          >
            Delete
          </button>
        </div>
        <div>
          <div className="section-title">Fires ({summary.fires?.length ?? 0})</div>
          {(summary.fires ?? []).map((f, i) => (
            <div key={i} className="small">
              <span className="mono">{f.trigger}</span>/{f.label} at {fmtTick(f.t)} ({fmtTick(f.window[0])}–{fmtTick(f.window[1])})
            </div>
          ))}
        </div>
        <div>
          <div className="section-title">Files ({detail.data.files.length})</div>
          {detail.data.files.map((f) => (
            <div key={f.name} className={`list-item${file === f.name ? " sel" : ""}`} onClick={() => open(f.name)}>
              <span className="name mono">{f.name}</span>
              <span className="faint small">{f.size} B</span>
            </div>
          ))}
        </div>
      </div>
      <div className="col" style={{ minHeight: 0, padding: 10 }}>
        {file ? <pre className="artifact mono">{text}</pre> : <Empty title="Pick a file to view the baseline content" />}
      </div>
    </Split>
  );
}

function SuggestDialog({
  projectId,
  onClose,
  onSuggested,
}: {
  projectId: string;
  onClose: () => void;
  onSuggested: (filename: string, yaml: string) => void;
}) {
  const runs = useRuns(projectId);
  const flights = useFlights();
  const [runId, setRunId] = useState<string>("");
  const [flightId, setFlightId] = useState<string>("");
  const [name, setName] = useState("");
  const [tol, setTol] = useState(0);
  const [golden, setGolden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (runs.data ?? []).filter((r) => r.status === "done");
  const run = done.find((r) => r.id === runId) ?? null;
  useEffect(() => {
    if (!runId && done.length) setRunId(done[0].id);
  }, [done, runId]);
  useEffect(() => {
    if (run && (!flightId || !run.flight_ids.includes(flightId))) setFlightId(run.flight_ids[0] ?? "");
  }, [run, flightId]);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await suggestScenario(projectId, { run_id: runId, flight_id: flightId, name: name.trim() || undefined, golden, tol });
      const fname = `${(r.scenario.name || "scenario").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")}.yaml`;
      onSuggested(fname, r.yaml);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Scenario from a run"
      onClose={onClose}
      width={520}
      footer={
        <>
          <span className="dim small">The result of the run becomes the expectation: fires, ticks, windows and file count.</span>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={go} disabled={busy || !runId || !flightId}>
            {busy ? <Spinner /> : "Create"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label>Run</label>
        <select className="inp" value={runId} onChange={(e) => setRunId(e.target.value)}>
          {done.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label || r.id} · {fmtDate(r.created_at)} · {r.summary?.fires ?? 0} fires
            </option>
          ))}
        </select>
        <label>Flight</label>
        <select className="inp" value={flightId} onChange={(e) => setFlightId(e.target.value)}>
          {(run?.flight_ids ?? []).map((f) => (
            <option key={f} value={f}>
              {flights.data?.find((x) => x.id === f)?.tail ?? ""} {f}
            </option>
          ))}
        </select>
        <label>Name</label>
        <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="(tail + flight id)" />
        <label>Tick tolerance</label>
        <input className="inp" type="number" min={0} value={tol} onChange={(e) => setTol(Number(e.target.value))} style={{ width: 90 }} />
        <label>Golden</label>
        <label className="row small">
          <input type="checkbox" checked={golden} onChange={(e) => setGolden(e.target.checked)} /> compare rendered files against a baseline
        </label>
      </div>
      {!done.length ? <div className="dim small" style={{ marginTop: 8 }}>No finished runs for this project yet.</div> : null}
      {error ? <div className="problem err" style={{ marginTop: 8 }}>{error}</div> : null}
    </Modal>
  );
}
